import { sharedExtensionCards } from "backend/src/common/gameData/extensions.shared";
import {
  ExtensionType,
  SimplifiedExtensionCard,
} from "backend/src/common/types/ExtensionCard";
import { ExtensionCardModelType } from "store/models/ExtensionCard";

import { Card } from "components/Card/Card";

import styles from "./styles.module.scss";
import { useHover } from "hooks/useHover";
import { ScrollYoyo } from "components/ScrollYoyo/ScrollYoyo";
import { extensionImages } from "./images";
import { canExtensionBeUsed } from "backend/src/common/utils/canExtensionBeUsed";
import { store } from "store/store";
import { GameState } from "backend/src/common/types/roomState";
import { useExtension } from "socket/events/useExtension";
import { getExtensionAdditionalFood } from "backend/src/common/utils/getExtensionAdditionalFood";
import { getExtensionContext } from "utils/context/getExtensionContext";
import { numberWithSign } from "utils/numberWithSign";
import { useI18n } from "i18n/I18nProvider";
import { getExtensionTexts } from "backend/src/common/i18n";

type ExtensionCardSimpleProps = {
  animalIndex?: number;
  extensionIndex?: number;
  isFull?: boolean;
  other?: boolean;
  onUse?: () => void;
  card: ExtensionCardModelType;
};

export const ExtensionCardSimple: React.FC<ExtensionCardSimpleProps> = ({
  card,
  animalIndex,
  extensionIndex,
  other,
  onUse,
  isFull = false,
}) => {
  const { locale, t } = useI18n();
  const extensionData = sharedExtensionCards[card.visibleType];
  const extensionTexts = getExtensionTexts(locale, card.visibleType);
  const [isHovered, onMouseOver, onMouseOut] = useHover();

  let afterTitle = "";
  const extensionFood = getExtensionAdditionalFood(getExtensionContext(card));
  if (extensionFood) {
    afterTitle = numberWithSign(extensionFood);
  }
  if (card.data?.storedFood) {
    afterTitle = `${card.data.storedFood}`;
  }

  return (
    <Card
      title={extensionTexts.name}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      hoverable
      afterTitle={afterTitle}
      afterTitleColor={card.data?.storedFood ? "#8d7a00" : "#6b0101"}
      red={card.visibleType === ExtensionType.predator}
      pink={extensionData.canBeAddedToEnemyAnimal}
      yellow={card.visibleType === ExtensionType.fat}
      used={card.wasUsedInCurrentStage}
      disabled={card.isDisabledByTumor}
    >
      <div className={styles.cardContent}>
        <ScrollYoyo active={isHovered} className={styles.description}>
          {extensionTexts.description}
        </ScrollYoyo>
        {extensionImages[card.visibleType] && (
          <div className={styles.image}>
            <img
              src={
                extensionImages[card.visibleType]?.[isFull ? "full" : "empty"]
              }
            />
          </div>
        )}
        {!other &&
          typeof animalIndex !== "undefined" &&
          typeof extensionIndex !== "undefined" &&
          !!extensionData.canBeUsedOnStages?.length && (
            <button
              className={styles.use}
              onClick={() => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                useExtension(animalIndex, extensionIndex);
                onUse?.();
              }}
              disabled={
                !store.iAmActiveUser ||
                !canExtensionBeUsed(
                  store.gameState as GameState,
                  card as SimplifiedExtensionCard,
                )
              }
            >
              {t("extension.use")}
            </button>
          )}
        {card.wasPlacedByUser && extensionData.canBeAddedToEnemyAnimal && (
          <div className={styles.wasPlacedByUser}>
            {t("extension.giftFrom", {
              playerName:
                store.players.find(
                  (player) => player.id === card.wasPlacedByUser,
                )?.meta.name || card.wasPlacedByUser,
            })}
          </div>
        )}
      </div>
    </Card>
  );
};
